// src/pages/RoleSelectionPage.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Building2, Calendar } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../services/apiClient';
import styles from './RoleSelectionPage.module.css';

export default function RoleSelectionPage() {
  const { user, retryFetchProfile } = useAuth();
  const navigate = useNavigate();
  const pendingRole = localStorage.getItem('pendingRole') || 'customer';
  const [role, setRole] = useState(pendingRole);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasAutoSubmitted, setHasAutoSubmitted] = useState(false);

  const handleSubmitWithRole = React.useCallback(async (selectedRole) => {
    if (!user) return;
    try {
      setLoading(true);
      setError('');
      // The API sets the role (once, server-side). The browser cannot write
      // profiles.role directly.
      await apiFetch('/v1/me/role', { method: 'POST', body: { role: selectedRole } });
      localStorage.removeItem('pendingRole');
      await retryFetchProfile();
      navigate('/dashboard');
    } catch (err) {
      console.error('Error setting role:', err);
      setError('Failed to set role. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [user, navigate, retryFetchProfile]);

  React.useEffect(() => {
    const savedRole = localStorage.getItem('pendingRole');
    if (savedRole && user && !loading && !hasAutoSubmitted) {
      setHasAutoSubmitted(true);
      handleSubmitWithRole(savedRole);
    }
  }, [user, loading, hasAutoSubmitted, handleSubmitWithRole]);

  const ROLES = [
    { value: 'customer', Icon: User,      label: 'Customer', desc: 'Book appointments with businesses' },
    { value: 'business', Icon: Building2, label: 'Business', desc: 'Manage appointments with customers' },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.shell}>
        <div className={styles.card}>
          <div className={styles.header}>
            <div className={styles.header__iconBadge}>
              <Calendar size={32} aria-hidden="true" />
            </div>
            <h1 className={styles.header__title}>Welcome to Vennio!</h1>
            <p className={styles.header__subtitle}>Infrastructure for Time</p>
          </div>

          <div className={styles.body}>
            {error && <div className={styles.errorBox}>{error}</div>}

            <p className={styles.roleQuestion}>I am a…</p>

            <div className={styles.roleGrid}>
              {ROLES.map(({ value, Icon, label, desc }) => {
                const selected = role === value;
                return (
                  <button
                    key={value}
                    onClick={() => setRole(value)}
                    className={`${styles.roleBtn} ${selected ? styles['roleBtn--selected'] : ''}`}
                  >
                    <Icon
                      size={32}
                      className={`${styles.roleIcon} ${selected ? styles['roleIcon--selected'] : ''}`}
                      aria-hidden="true"
                    />
                    <div className={styles.roleName}>{label}</div>
                    <p className={styles.roleDesc}>{desc}</p>
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => handleSubmitWithRole(role)}
              disabled={loading}
              className={`btn-primary ${styles.submitBtn}`}
            >
              {loading ? 'Setting up…' : 'Continue'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
